import { useEffect, useMemo, useState } from "react";

import { MicIcon, speakingScoreClass } from "./SpeakingPanel";
import useSentenceSpeaking from "./useSentenceSpeaking";

// 리더스북 본문 따라 읽기(2026-10-18 대표). 쪽 위에는 지금 녹음할 문장 하나만 형광펜으로 칠하고, 마이크는 QR 자리
// 스피커 옆에 둔다. 다른 문장을 읽고 싶으면 그 문장을 누른다(형광펜이 옮겨 가고 그 문장을 들려준다).
//
// - 형광펜 자리(sentence.boxes)는 출제 때 서버가 쪽 그림을 훑어 만든다(api/assignment/sentence_boxes.py).
//   없으면 문장 끝(지인에듀 sentences[].end)에 점만 찍는다.
// - 마이크는 아이가 눌러야 열린다(iOS 는 그 밖에서 열면 막힌다). 문장을 들은 뒤에는 마이크가 깜빡여 차례를 알린다.
// - 선생님이 출제 때 고른 점수(passScore, 기본 70) 이상 읽으면 다음 문장으로 넘어간다. 받아쓴 글은 보이지 않는다.

const DEFAULT_PASS_SCORE = 70;
const keyOf = (audioUrl, sentenceIndex) => `${audioUrl}#${sentenceIndex}`;

export const useReadAloud = ({ audioList, speaking, onResult, isEditable, passScore = DEFAULT_PASS_SCORE }) => {
  const sentenceList = useMemo(
    () =>
      audioList.flatMap((audio) =>
        (audio.sentences || []).map((sentence) => ({ audio, sentence, key: keyOf(audio.url, sentence.idx) }))
      ),
    [audioList]
  );
  const [position, setPosition] = useState(0);
  const [readyKey, setReadyKey] = useState(null);
  const current = sentenceList[Math.min(position, Math.max(0, sentenceList.length - 1))] || null;

  const { isSupported, listeningKey, playingKey, play, stopPlaying, record } = useSentenceSpeaking({
    sentenceList: current ? current.audio.sentences : [],
    onResult: (sentence, accuracy, transcript, key) => {
      const target = sentenceList.find((one) => one.key === key);
      if (!target) return;
      onResult(target.audio.url, sentence.idx, accuracy, transcript);
      if (accuracy >= passScore) setPosition((value) => Math.min(value + 1, sentenceList.length - 1));
    },
  });
  const canSpeak = isSupported && isEditable;

  // 면을 넘기면(문장 목록이 바뀌면) 처음 문장부터
  const listKey = sentenceList.map((one) => one.key).join("|");
  useEffect(() => {
    setPosition(0);
    setReadyKey(null);
  }, [listKey]);

  // 문장을 누르면 그 문장으로 옮기고 들려준다. 다 들으면 마이크가 깜빡인다.
  const select = (entry) => {
    const index = sentenceList.findIndex((one) => one.key === entry.key);
    if (index < 0 || listeningKey !== null) return;
    setPosition(index);
    setReadyKey(null);
    if (playingKey === entry.key) {
      stopPlaying();
      return;
    }
    play(entry.sentence, entry.key, () => {
      if (canSpeak) setReadyKey(entry.key);
    });
  };

  const handleMic = () => {
    if (!current) return;
    setReadyKey(null);
    record(current.sentence, current.key, current.audio.sentences);
  };

  const spoken = current ? (speaking[current.audio.url] || {})[current.sentence.idx] : null;
  return {
    sentenceList,
    current,
    canSpeak,
    select,
    handleMic,
    isPlaying: Boolean(current) && playingKey === current.key,
    isListening: Boolean(current) && listeningKey === current.key,
    isBusy: listeningKey !== null,
    isReady: Boolean(current) && readyKey === current.key && listeningKey !== current.key,
    score: spoken?.best_accuracy ?? spoken?.last_accuracy,
  };
};

// 쪽 위: 지금 문장 형광펜 + 문장마다 누를 자리(보이지 않음)
export const SentenceHighlight = ({ readAloud, pageNumber }) => {
  const isOnPage = (sentence) => !sentence.end || sentence.end.page === undefined || sentence.end.page === pageNumber;
  const current = readAloud.current;
  const currentBoxList = current && isOnPage(current.sentence) ? current.sentence.boxes || [] : [];
  return (
    <div className="sentence-layer">
      {readAloud.sentenceList
        .filter((entry) => isOnPage(entry.sentence))
        .flatMap((entry) =>
          (entry.sentence.boxes || []).map((box, index) => (
            <button
              type="button"
              key={`${entry.key}-${index}`}
              className="sentence-hit"
              style={{ left: `${box.x * 100}%`, top: `${box.y * 100}%`, width: `${box.w * 100}%`, height: `${box.h * 100}%` }}
              onClick={() => readAloud.select(entry)}
              aria-label="이 문장 듣기"
            />
          ))
        )}
      {currentBoxList.map((box, index) => (
        <span
          key={index}
          className={`sentence-highlight${readAloud.isListening ? " is-listening" : ""}`}
          style={{ left: `${box.x * 100}%`, top: `${box.y * 100}%`, width: `${box.w * 100}%`, height: `${box.h * 100}%` }}
        />
      ))}
      {current && isOnPage(current.sentence) && !currentBoxList.length && current.sentence.end && (
        <span
          className="sentence-dot"
          style={{ left: `${current.sentence.end.x * 100}%`, top: `${current.sentence.end.y * 100}%` }}
        />
      )}
    </div>
  );
};

// 스피커 옆 마이크. 누르면 형광펜 문장을 녹음한다. 듣는 중에 다시 누르면 '다 읽었어요'.
export const ReadAloudMic = ({ readAloud, style }) => {
  if (!readAloud.current || !readAloud.canSpeak) return null;
  const score = readAloud.score;
  return (
    <div className="read-aloud" style={style}>
      <button
        type="button"
        className={`read-aloud__mic${readAloud.isListening ? " is-listening" : ""}${readAloud.isReady ? " is-ready" : ""}`}
        onClick={readAloud.handleMic}
        disabled={readAloud.isBusy && !readAloud.isListening}
        aria-label={readAloud.isListening ? "다 읽었어요" : "형광펜 문장 녹음하기"}
      >
        {readAloud.isListening ? "다 읽었어요" : <MicIcon size={20} />}
      </button>
      {typeof score === "number" && !readAloud.isListening && (
        <span className={`read-aloud__score ${speakingScoreClass(score)}`}>{score}</span>
      )}
    </div>
  );
};
